import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import { Link as RouterLink, useHistory } from "react-router-dom";
import dayjs from "dayjs";

import MeetingEmptyStateIcon from "src/icons/MeetingEmptyStateIcon";
import { useEvents } from "src/queries/useEvents";
import Table, { TableLoading } from "src/componentsV2/tables/Table";
import WebinarsFilterBar, {
  WebinarQuery,
} from "src/componentsV2/WebinarsFilterBar";
import useUrlQueryV2 from "src/hooks/useUrlQueryV2";
import { toDateFilters } from "../meetings";
import { useWebinarsCount } from "src/queries/useWebinarsCount";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Pages } from "src/componentsV2/Pagination";
import PrimaryButton from "src/componentsV2/buttons/PrimaryButton";
import { INVITE_STYLE } from "src/meetingTypes/invite/props";
import { meetingStatusOptionFromValue } from "../../types";
import { toInstanceStatusColor } from "src/meetings/props";
import { useCreateMeetingTemplate } from "src/mutations/useCreateMeetingTemplate";
import { useUpdateMeetingTemplateType } from "src/mutations/useUpdateMeetingTemplateType";
import { useSnackbar } from "src/hooks";

const defaultWebinarQuery: WebinarQuery = {
  hostEmail: "",
  meetingStatusFilter: [],
  meetingIdFilter: [],
  paging: {
    limit: 100,
    offset: 0,
  },
  sort: {
    field: "lastActivity",
    order: "desc",
  },
  tags: [],
  tagFilterSearch: "",
  templateFilterSearch: "",
  template: [],
  date: {
    field: "startTime",
    range: [null, null],
  },
};

type EventsPageTabs = "Promoting" | "Completed" | "Incomplete" | "All";

function EventsPage() {
  const history = useHistory();
  const urlQuery = useUrlQueryV2();
  const [openSnackbar] = useSnackbar();

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState<WebinarQuery>(defaultWebinarQuery);
  const [templateFilter, setTemplateFilter] = useState<number[]>([]);
  const [tab, setTab] = useState<EventsPageTabs>("All");
  const [createEventInProgress, setCreateEventInProgress] = useState(false);

  const createMeetingTemplate = useCreateMeetingTemplate();
  const updateMeetingTemplateType = useUpdateMeetingTemplateType();

  const eventsCount = useWebinarsCount({
    filter: {
      meetingStatusFilter: [],
      hostEmail: query.hostEmail,
      tagFilter: query.tags.map((t) => t.id),
      meetingTypeFilter: templateFilter,
      dateFilter: toDateFilters(query).dateFilter,
    },
  });

  const { data, loading, error } = useEvents({
    filter: {
      meetingStatusFilter: query.meetingStatusFilter,
      hostEmail: query.hostEmail,
      tagFilter: query.tags.map((t) => t.id),
      meetingTypeFilter: templateFilter,
      dateFilter: toDateFilters(query).dateFilter,
    },
    paging: {
      limit: pageSize,
      offset: page - 1,
    },
    sort: { field: "meetingTime", order: "desc" },
  });

  const total = data?.total || eventsCount.data?.total || 0;
  const currentPageData = data?.data || [];
  const totalPages = Math.ceil(total / pageSize);

  useEffect(() => {
    if (total > 0 && page > totalPages) {
      setPage(1);
    }
  }, [total, pageSize, page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    const templateQuery = urlQuery.getAll("templateId");
    setTemplateFilter(
      templateQuery.map((templateQueryItem) => Number(templateQueryItem)),
    );
  }, [urlQuery]);

  const handleCreateEvent = async () => {
    try {
      setCreateEventInProgress(true);
      const meetingTemplateResponse = await createMeetingTemplate();

      await updateMeetingTemplateType(
        meetingTemplateResponse.id,
        INVITE_STYLE.WEBINAR,
      );

      history.push(`/meeting-templates/edit/${meetingTemplateResponse.id}`);
    } catch (error) {
      openSnackbar("Failed to create event");
      console.error(error);
    } finally {
      setCreateEventInProgress(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: "#f1f1f1", minHeight: "100%" }}>
      <Container maxWidth="xl" disableGutters sx={{ px: "30px", pb: "65px" }}>
        <Stack
          sx={{
            py: "35px",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h1"
            sx={{ fontSize: "40px", fontWeight: "bold" }}
          >
            Events
          </Typography>
          <PrimaryButton
            disabled={createEventInProgress}
            onClick={handleCreateEvent}
          >
            + Create Event
          </PrimaryButton>
        </Stack>

        <Stack spacing={2}>
          <Paper elevation={0} sx={{ p: 2 }}>
            <WebinarsFilterBar
              filters={query}
              templateFilter={templateFilter}
              onFilterUpdate={(newFilters) => setQuery(newFilters)}
              onTemplateFilterUpdate={(templates) => {
                // Filter to only include webinar templates
                const webinarTemplates = templates.filter(
                  (template) => template.inviteStyle === INVITE_STYLE.WEBINAR,
                );

                urlQuery.delete("templateId");
                webinarTemplates.forEach((templateItem) =>
                  urlQuery.append("templateId", templateItem.id.toString()),
                );
                history.push({
                  search: `?${urlQuery.toString()}`,
                });
              }}
              onReset={() => {
                setQuery(defaultWebinarQuery);
                history.push({
                  search: "",
                });
              }}
            />
          </Paper>

          <Paper elevation={0}>
            <Box>
              <Stack
                sx={{
                  px: "20px",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <Tabs
                  value={tab}
                  sx={{
                    textDecoration: 0,
                    borderBottom: "1px solid #CDD6DD",
                  }}
                  TabIndicatorProps={{
                    style: { backgroundColor: "transparent" },
                  }}
                  onChange={(_, value: EventsPageTabs) => {
                    switch (value) {
                      case "Promoting":
                        setQuery({
                          ...query,
                          meetingStatusFilter: ["scheduling"],
                        });
                        break;
                      case "Completed":
                        setQuery({
                          ...query,
                          meetingStatusFilter: ["completed"],
                        });
                        break;
                      case "Incomplete":
                        setQuery({
                          ...query,
                          meetingStatusFilter: ["initialized"],
                        });
                        break;
                      case "All":
                        setQuery({
                          ...query,
                          meetingStatusFilter: [],
                        });
                        break;
                    }
                    setTab(value);
                  }}
                >
                  <Tab
                    label={`All (${eventsCount.data?.total ?? 0})`}
                    value="All"
                    sx={{ textTransform: "none" }}
                  />
                  <Tab
                    label={`Promoting (${
                      eventsCount.data?.promotingTotal ?? 0
                    })`}
                    value="Promoting"
                    sx={{ textTransform: "none" }}
                  />
                  <Tab
                    label={`Completed (${
                      eventsCount.data?.completedTotal ?? 0
                    })`}
                    value="Completed"
                    sx={{ textTransform: "none" }}
                  />
                  <Tab
                    label={`Incomplete (${eventsCount.data?.incompleteTotal ?? 0})`}
                    value="Incomplete"
                    sx={{ textTransform: "none" }}
                  />
                </Tabs>
              </Stack>
              {!loading && total === 0 && (
                <Stack
                  sx={{
                    my: "140px",
                    mx: "auto",
                    maxWidth: "800px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "20px",
                    textAlign: "center",
                  }}
                >
                  <MeetingEmptyStateIcon />
                  <Typography
                    color="primary.dark"
                    sx={{ fontSize: "30px", fontWeight: "bold" }}
                  >
                    No events found
                  </Typography>
                </Stack>
              )}
              {loading && <TableLoading />}
              {!loading && !error && total > 0 && (
                <>
                  <Table
                    loading={loading}
                    data={currentPageData}
                    columns={[
                      {
                        id: "id",
                        label: "Id",
                        component: (row) => (
                          <Link component={RouterLink} to={`/events/${row.id}`}>
                            {row.id}
                          </Link>
                        ),
                      },
                      {
                        id: "meeting_start_time",
                        label: `Time (${dayjs().format("z")})`,
                        component: (row) => {
                          const startTime = row.meeting_start_time
                            ? dayjs(row.meeting_start_time)
                            : null;
                          return (
                            startTime && (
                              <Stack>
                                <Typography variant="caption">
                                  {startTime.format("ddd, MMM D")}
                                </Typography>
                                <Typography variant="caption">
                                  {startTime.format("h:mm A")}
                                </Typography>
                              </Stack>
                            )
                          );
                        },
                        width: "150px",
                      },
                      {
                        id: "meeting_definition_name",
                        label: "Event Name",
                        component: (row) => (
                          <Stack sx={{ gap: "6px" }}>
                            <Typography fontSize="14px">
                              {row.meeting_definition_name}
                            </Typography>
                            {row.attempts?.total > 0 && (
                              <Stack sx={{ flexDirection: "row", gap: "4px" }}>
                                {new Array(row.attempts.current ?? 0)
                                  .fill(null)
                                  .map((_, index) => (
                                    <Box
                                      key={`attempt-${row.id}-${index}`}
                                      sx={{
                                        borderRadius: 999,
                                        height: "12px",
                                        width: "12px",
                                        backgroundColor: "primary.main",
                                      }}
                                    ></Box>
                                  ))}
                                {new Array(
                                  Math.max(
                                    (row.attempts.total ?? 0) -
                                      (row.attempts.current ?? 0),
                                    0,
                                  ),
                                )
                                  .fill(null)
                                  .map((_, index) => (
                                    <Box
                                      key={`attempt-${row.id}-remaining-${index}`}
                                      sx={{
                                        borderRadius: 999,
                                        height: "12px",
                                        width: "12px",
                                        backgroundColor: "#CDD6DD",
                                      }}
                                    ></Box>
                                  ))}
                              </Stack>
                            )}
                          </Stack>
                        ),
                      },
                      {
                        id: "host",
                        label: "Organizer",
                        component: (row) => (
                          <Stack>
                            <Typography variant="caption">
                              {row.host.first_name} {row.host.last_name}
                            </Typography>
                            <Typography variant="caption">
                              {row.host.email}
                            </Typography>
                          </Stack>
                        ),
                      },
                      {
                        id: "status",
                        label: "Status",
                        component: (row) => (
                          <Chip
                            label={
                              meetingStatusOptionFromValue(row.status)?.label ||
                              "Unknown"
                            }
                            size="small"
                            sx={{
                              backgroundColor:
                                toInstanceStatusColor(row.status) || "#9e9e9e",
                              color: "#fff",
                              fontWeight: "bold",
                            }}
                          />
                        ),
                      },
                      {
                        id: "accepted",
                        label: "Accepted",
                        component: (row) => (
                          <Stack sx={{ gap: "6px" }}>
                            <Typography fontSize="14px">
                              {row.attendee_stats?.accepted}
                            </Typography>
                          </Stack>
                        ),
                      },
                    ]}
                  />
                  <Box sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack
                        sx={{
                          flexDirection: "row",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <Typography>Rows per page:</Typography>
                        <Select
                          variant="standard"
                          labelId="rows-per-page-label"
                          id="rows-per-page"
                          value={pageSize}
                          onChange={(event) =>
                            setPageSize(Number(event.target.value))
                          }
                          label="Rows per page"
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </Stack>

                      <Stack sx={{ flexDirection: "row", gap: "10px" }}>
                        <PrimaryButton
                          disabled={page <= 1}
                          onClick={() => setPage(Math.max(page - 1, 1))}
                        >
                          <ChevronLeft />
                        </PrimaryButton>
                        <Pages
                          pages={Math.ceil(total / pageSize)}
                          currentPage={page}
                          onPageSelect={(newPage) => setPage(newPage)}
                        />

                        <PrimaryButton
                          disabled={page >= totalPages}
                          onClick={() =>
                            setPage(Math.min(page + 1, totalPages))
                          }
                        >
                          <ChevronRight />
                        </PrimaryButton>
                      </Stack>
                    </Stack>
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

export default EventsPage;
